import React, { useEffect, useMemo, useRef, useState } from "react";
import Calendar from "react-calendar";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { getLastThursday, esMismoDia } from "utils/jueves";
import { cn } from "./cn";

/** Formato con el que los formularios de la app manejan la fecha. */
const FORMATO = "dd/MM/yyyy";

/** Cuántos jueves recientes se ofrecen como acceso directo. */
const CANTIDAD_JUEVES = 4;

export interface SelectorFechaProps {
  /** Fecha en formato dd/MM/yyyy. */
  value: string;
  onChange: (valor: string) => void;
  label?: string;
  /** Avisa cuando se abre o cierra el calendario, para que el contenedor
   *  pueda hacerle lugar sin que el modal salte. */
  onCalendarioChange?: (abierto: boolean) => void;
  className?: string;
}

const parsear = (valor: string): Date | null => {
  if (!valor?.trim()) return null;
  const fecha = parse(valor, FORMATO, new Date());
  return isValid(fecha) ? fecha : null;
};

/**
 * Selector de fecha del grupo.
 *
 * El calendario no era feo por el calendario: era feo porque se abría siempre.
 * Como se juega los jueves, en la práctica la fecha buscada es uno de los
 * últimos cuatro, así que esos van como accesos directos y el calendario queda
 * detrás de "Otra fecha" para los casos raros. La mayoría de las veces el modal
 * no cambia de alto ni una vez.
 */
const SelectorFecha: React.FC<SelectorFechaProps> = ({
  value,
  onChange,
  label = "Fecha",
  onCalendarioChange,
  className,
}) => {
  const [mostrarCalendario, setMostrarCalendario] = useState(false);
  const calendarioRef = useRef<HTMLDivElement>(null);

  const seleccionada = parsear(value);

  const jueves = useMemo(() => {
    const base = getLastThursday();
    return Array.from({ length: CANTIDAD_JUEVES }, (_, i) => {
      const fecha = new Date(base);
      fecha.setDate(base.getDate() - i * 7);
      return fecha;
    });
  }, []);

  /** Una fecha elegida a mano que no es ninguno de los jueves ofrecidos. */
  const fueraDeLista =
    seleccionada && !jueves.some((j) => esMismoDia(j, seleccionada))
      ? seleccionada
      : null;

  const abrirCalendario = (abierto: boolean) => {
    setMostrarCalendario(abierto);
    onCalendarioChange?.(abierto);
  };

  const elegir = (fecha: Date) => {
    onChange(format(fecha, FORMATO));
    abrirCalendario(false);
  };

  // Cerrar al hacer clic afuera, igual que antes
  useEffect(() => {
    if (!mostrarCalendario) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        calendarioRef.current &&
        !calendarioRef.current.contains(e.target as Node)
      ) {
        abrirCalendario(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarCalendario]);

  const chipBase =
    "tabular h-9 rounded-control border px-3 text-[13px] font-semibold transition-colors duration-150";
  const chipActivo = "border-brand-500 bg-brand-500 text-white";
  const chipInactivo =
    "border-gray-200 bg-white text-gray-900 hover:border-gray-300 hover:bg-gray-50 " +
    "dark:border-white/10 dark:bg-navy-900 dark:text-white dark:hover:bg-white/5";

  return (
    <div className={cn("w-full text-left", className)}>
      <label className="mb-1.5 block text-[13px] font-medium text-gray-900 dark:text-gray-200">
        {label}
      </label>

      {/* Accesos directos. Envuelven solos en pantallas angostas. */}
      <div className="flex flex-wrap gap-1.5">
        {/* Una fecha elegida a mano se muestra, pero no es un control:
            para volver a ella no hay que hacer nada. */}
        {fueraDeLista && (
          <span className={cn(chipBase, chipActivo, "inline-flex items-center")}>
            {format(fueraDeLista, "dd/MM")}
          </span>
        )}

        {jueves.map((fecha) => {
          const activo = Boolean(seleccionada && esMismoDia(fecha, seleccionada));
          return (
            <button
              key={fecha.toISOString()}
              type="button"
              aria-pressed={activo}
              onClick={() => elegir(fecha)}
              className={cn(chipBase, activo ? chipActivo : chipInactivo)}
            >
              {format(fecha, "dd/MM")}
            </button>
          );
        })}

        <button
          type="button"
          aria-expanded={mostrarCalendario}
          onClick={() => abrirCalendario(!mostrarCalendario)}
          className={cn(
            chipBase,
            "inline-flex items-center gap-1.5",
            mostrarCalendario
              ? "border-gray-300 bg-gray-100 text-gray-900 dark:border-white/20 dark:bg-white/10 dark:text-white"
              : chipInactivo
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          Otra
        </button>
      </div>

      {mostrarCalendario && (
        <div
          ref={calendarioRef}
          className="mt-2.5 flex justify-center rounded-card border border-gray-200/80 bg-white p-3 dark:border-white/10 dark:bg-navy-800"
        >
          <Calendar
            onChange={(fecha: Date) => elegir(fecha)}
            value={seleccionada ?? undefined}
            locale="es-ES"
          />
        </div>
      )}
    </div>
  );
};

export default SelectorFecha;
